import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaArrowDownRegular, FaArrowUpRegular } from '@enechain/ecloud-icons'

import type { CardModel } from '~/pages/SupplyCapacityChange/models/supplyCapacity'
import {
  fmtMMDD,
  fmtMw,
  pressureOf,
} from '~/pages/SupplyCapacityChange/models/supplyCapacity'

const borderColor = LightModeSemanticColors.Border.lowEmphasis.value
const accentBorder = LightModeSemanticColors.Border.accentPrimary.value
const accentText = LightModeSemanticColors.Text.accentPrimary.value
const tealColor = LightModeSemanticColors.Brand.teal.value

type CardProps = {
  /** 当日 / 翌日 ラベル */
  label: string
  /** 当日=accent / 翌日=teal */
  variant: 'today' | 'tomorrow'
  date: Date
  model: CardModel
}

const ImpactCard = ({
  label,
  variant,
  date,
  model,
}: CardProps): React.ReactElement => {
  const { t } = useTranslation()
  const { startMw, endMw, net } = model
  const dayColor = variant === 'today' ? accentText : tealColor
  const dayBorder = variant === 'today' ? accentBorder : tealColor
  // 価格圧力の符号判定は model の pressureOf に集約 (= 下落圧力なら positive/緑/下矢印)
  const { isFalling, tone } = pressureOf(net)
  const netColor =
    tone === 'positive' ? 'Text.positiveMid' : 'Text.negativeHigh'
  const arrowBg =
    tone === 'positive' ? 'Surface.positiveLow' : 'Surface.negativeLow'

  return (
    <Box
      flex={1}
      padding="8px 12px"
      fontSize="md"
      border={`1px solid ${borderColor}`}
      borderRadius="lg"
      backgroundColor="Surface.primary"
    >
      <Box
        marginBottom={1}
        paddingBottom="4px"
        color={dayColor}
        fontWeight="bold"
        borderBottom={`2px solid ${dayBorder}`}
      >
        {`${label} ${fmtMMDD(date)}`}
      </Box>
      <Flex justifyContent="space-between" paddingY="2px">
        <Text as="span" color="Text.negativeHigh" fontWeight="bold">
          {t('page:supplyCapacityChange.impact.stopStart')}
        </Text>
        <Text as="span" color="Text.negativeHigh" fontWeight="bold">
          {`-${Math.round(startMw).toLocaleString()} MW`}
        </Text>
      </Flex>
      <Flex justifyContent="space-between" paddingY="2px">
        <Text as="span" color="Text.positiveMid" fontWeight="bold">
          {t('page:supplyCapacityChange.impact.stopEnd')}
        </Text>
        <Text as="span" color="Text.positiveMid" fontWeight="bold">
          {`+${Math.round(endMw).toLocaleString()} MW`}
        </Text>
      </Flex>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        marginTop="4px"
        paddingTop="4px"
        color={netColor}
        fontWeight="bold"
        borderTop={`1px solid ${borderColor}`}
      >
        <Text as="span">{t('page:supplyCapacityChange.impact.netChange')}</Text>
        <Text as="span" fontSize="lg">{`${fmtMw(net)} MW`}</Text>
      </Flex>
      {/* eslint-disable-next-line ebalance/no-adhoc-typography -- 価格圧力 pill sm/bold (design :60 .scc-impact-arrow = sm/700 一致。状態 pill defer) */}
      <Flex
        alignItems="center"
        justifyContent="center"
        gap={1}
        marginTop="4px"
        padding="4px"
        color={netColor}
        fontSize="sm"
        fontWeight="bold"
        borderRadius="base"
        backgroundColor={arrowBg}
      >
        {t('page:supplyCapacityChange.impact.pricePressure')}
        {isFalling ? (
          <FaArrowDownRegular boxSize="12px" />
        ) : (
          <FaArrowUpRegular boxSize="12px" />
        )}
        {isFalling
          ? t('page:supplyCapacityChange.impact.priceFalling')
          : t('page:supplyCapacityChange.impact.priceRising')}
      </Flex>
    </Box>
  )
}

type Props = {
  today: Date
  tmr: Date
  todayModel: CardModel
  tmrModel: CardModel
}

export const ImpactCards = ({
  today,
  tmr,
  todayModel,
  tmrModel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <Flex flexDirection="column" flex={1} gap={2}>
      <ImpactCard
        date={today}
        label={t('page:supplyCapacityChange.impact.today')}
        model={todayModel}
        variant="today"
      />
      <ImpactCard
        date={tmr}
        label={t('page:supplyCapacityChange.impact.tomorrow')}
        model={tmrModel}
        variant="tomorrow"
      />
    </Flex>
  )
}
